
import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Mail } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

interface CourseNotificationDialogProps {
  courseId: string;
  courseName: string;
  isOpen: boolean;
  onClose: () => void;
}

const CourseNotificationDialog = ({ courseId, courseName, isOpen, onClose }: CourseNotificationDialogProps) => {
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();
  const { user } = useAuth();
  
  // Set email from user when available
  useEffect(() => {
    if (user?.email) {
      setEmail(user.email);
    }
  }, [user]);

  const isValidEmail = (email: string) => {
    return /\S+@\S+\.\S+/.test(email);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!isValidEmail(email)) {
      toast({
        title: "Invalid email",
        description: "Please enter a valid email address.",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsSubmitting(true);

      const { error } = await supabase.from("course_notifications").insert({
        course_id: courseId,
        email: email,
      });

      if (error) throw error;

      toast({
        title: "Success!",
        description: "You'll be notified when this course is available for enrollment.",
      });

      setEmail("");
      onClose();
    } catch (error: any) {
      console.error("Error submitting notification request:", error);
      toast({
        title: "Something went wrong",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Get notified</DialogTitle>
          <DialogDescription>
            Receive an email when "{courseName}" becomes available for enrollment.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 py-4">
          <div className="space-y-2">
            <Input
              type="email"
              placeholder="Enter your email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={!!user}
              required
            />
            {user && (
              <div className="p-3 bg-muted/20 rounded-md text-sm">
                You'll be notified at <span className="font-medium">{user.email}</span> when this course is available.
              </div>
            )}
          </div>

          <DialogFooter className="sm:justify-start">
            <Button type="submit" className="w-full" disabled={isSubmitting}>
              {isSubmitting ? "Submitting..." : (
                <>
                  <Mail className="mr-2 h-4 w-4" />
                  Notify Me
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default CourseNotificationDialog;
